<template>
  <div :class="$style.container">
    <div :class="$style.location">
      <div :class="$style.head">
        <p :class="$style.title">店舗を探す</p>
        <div>
          <AtomsConsumerButton v-if="showResetFilter" :class="$style.clear" @click="resetFilter">
            絞り込みを解除
          </AtomsConsumerButton>
        </div>
      </div>
      <div>
        <div :class="$style.location_button_area">
          <StoreLocationButton :class="$style.location_button" variant="primary" block @click="setLocation">
            現在地から探す
          </StoreLocationButton>
        </div>
        <p :class="$style.note">※お近くの店舗順に表示されます</p>
        <p v-if="locationErrorMessage" ref="locationErrorRef" :class="$style.error_message">{{
          locationErrorMessage
        }}</p>
      </div>
      <MoleculesConsumerFormItem>
        <template #title>都道府県で検索</template>
        <AtomsConsumerSelect
          v-model="prefectureRef"
          name="prefecture"
          placeholder="都道府県を選択"
          :options="prefectures"
          @change="updateFilterValue"
        />
      </MoleculesConsumerFormItem>
      <form action="#" :class="$style.form" autocomplete="off" enterkeyhint="search" @submit.prevent="keywordInput">
        <MoleculesConsumerFormItem>
          <template #title>キーワードで検索</template>
          <AtomsConsumerInput
            id="storeSearchTextInput"
            v-model="searchTextRef"
            name="searchText"
            type="text"
            placeholder="キーワードで検索"
            :max-length="100"
          />
        </MoleculesConsumerFormItem>
      </form>
    </div>
    <div :class="[$style.sidebar, { [$style.sp_filter]: !isMd, [$style.sp_open]: isOpenFilterSp }]">
      <AtomsConsumerButton :class="$style.close" @click="closeFilter" />
      <div :class="$style.filter_list">
        <p :class="$style.filter_title">絞り込み検索</p>

        <div :class="$style.form_section">
          <StoreFilteredTagList
            :products="productsQueryRef"
            :types="typeQueryRef"
            :scales="scaleQueryRef"
            :others-selects="othersSelectQueryRef"
            @remove-tag="removeTag"
          />
          <AtomsConsumerButton v-if="!isMd && showResetFilter" :class="$style.clear" @click="resetFilter">
            絞り込みを解除
          </AtomsConsumerButton>
        </div>

        <MoleculesConsumerAccordion icon="plus-minus" :is-open-by-default="!props.isMd" :class="$style.form_section">
          <template #title>取扱商品</template>
          <template #content>
            <div :class="$style.checkbox_list">
              <CommonMultiCheckBox
                v-model:value="productsQueryRef"
                name="productItems"
                :list="productItems"
                :is-required-text-form="false"
                @change="updateFilterValue"
              />
            </div>
          </template>
        </MoleculesConsumerAccordion>

        <MoleculesConsumerAccordion icon="plus-minus" :is-open-by-default="!props.isMd" :class="$style.form_section">
          <template #title>店舗形態</template>
          <template #content>
            <div :class="$style.checkbox_list">
              <CommonMultiCheckBox
                v-model:value="typeQueryRef"
                name="typeItems"
                :list="typeItems"
                :is-required-text-form="false"
                @change="updateFilterValue"
              />
            </div>
          </template>
        </MoleculesConsumerAccordion>

        <MoleculesConsumerAccordion icon="plus-minus" :is-open-by-default="!props.isMd" :class="$style.form_section">
          <template #title>店舗規模</template>
          <template #content>
            <div :class="$style.checkbox_list">
              <CommonMultiCheckBox
                v-model:value="scaleQueryRef"
                name="scaleItems"
                :list="scaleItems"
                :is-required-text-form="false"
                @change="updateFilterValue"
              />
            </div>
          </template>
        </MoleculesConsumerAccordion>

        <MoleculesConsumerAccordion icon="plus-minus" :is-open-by-default="!props.isMd" :class="$style.form_section">
          <template #title>その他のこだわり</template>
          <template #content>
            <div :class="$style.checkbox_list">
              <CommonMultiCheckBox
                v-model:value="othersSelectQueryRef"
                name="othersSelectItems"
                :list="othersSelectItems"
                :is-required-text-form="false"
                @change="updateFilterValue"
              />
            </div>
          </template>
        </MoleculesConsumerAccordion>
      </div>
      <AtomsConsumerButton v-if="!isMd" :class="$style.submit" @click="closeFilter">
        結果を表示（{{ listCount }}件）
      </AtomsConsumerButton>
    </div>
    <div v-if="isOpenFilterSp && !isMd" :class="$style.overlay_area" @click="emit('update:isOpenFilterSp', false)" />
  </div>
</template>

<script setup lang="ts">
import {
  FilterStoreType,
  StoreScaleType,
  StoreProductType,
  OthersSelectType,
} from "@tential/ec-gql-schema/master/store"
import { PREFECTURES } from "~/const"
import { useToast } from "~/state"
import { getLocation } from "~/utils/functions/location"

type CommonMultiCheckBoxItem = {
  key: string
  value: string
  disabled?: boolean
}

const emit = defineEmits<{
  (e: "update:isOpenFilterSp", isOpenFilterSp: boolean): void
}>()

const props = defineProps<{
  listCount: number
  isMd: boolean
  isOpenFilterSp: boolean
  locationErrorMessage: string
  updateLocationError: (message: string) => void
  updateIsLoading: (isLoading: boolean) => void
  scrollToFilter: () => void
}>()

const localValue = computed({
  get: () => props.isOpenFilterSp,
  set: (value) => emit("update:isOpenFilterSp", value),
})

const closeFilter = (): void => {
  localValue.value = false
}

const route = useRoute()
const toast = useToast()
const locationErrorRef = ref<HTMLElement>()
const searchTextRef = ref<string>("")
const prefectureRef = ref<string>("")
const latitudeRef = ref<number | undefined>(undefined)
const longitudeRef = ref<number | undefined>(undefined)
const productsQueryRef = ref<string[]>([])
const typeQueryRef = ref<string[]>([])
const scaleQueryRef = ref<string[]>([])
const othersSelectQueryRef = ref<string[]>([])

const setLocation = async () => {
  try {
    props.updateIsLoading(true)
    const location = await getLocation()
    latitudeRef.value = location.latitude
    longitudeRef.value = location.longitude
    updateFilterValue()
  } catch (errorMessage) {
    if (typeof errorMessage === "string") {
      toast?.showErrorToasted(errorMessage)
      props.updateLocationError(errorMessage)
    }
  } finally {
    props.updateIsLoading(false)
    props.scrollToFilter()
  }
}

const productItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return Object.entries(StoreProductType).map(([value, key]) => ({
    key,
    value,
  }))
})

const typeItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return Object.entries(FilterStoreType).map(([value, key]) => ({
    key,
    value,
  }))
})

const scaleItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return Object.entries(StoreScaleType).map(([value, key]) => ({
    key,
    value,
  }))
})

const othersSelectItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return Object.entries(OthersSelectType).map(([value, key]) => ({
    key,
    value,
  }))
})

const prefectures = computed(() => {
  if (prefectureRef.value) return ["選択を解除", ...PREFECTURES]
  return PREFECTURES
})

const showResetFilter = computed(() => {
  return (
    productsQueryRef.value.length ||
    typeQueryRef.value.length ||
    scaleQueryRef.value.length ||
    othersSelectQueryRef.value.length ||
    prefectureRef.value ||
    searchTextRef.value ||
    latitudeRef.value ||
    longitudeRef.value
  )
})

const createFilterQuery = () => {
  const query: { [key: string]: string } = { page: "1" }
  if (searchTextRef.value) query.searchText = searchTextRef.value
  if (prefectureRef.value === "選択を解除") prefectureRef.value = ""
  if (prefectureRef.value) query.prefecture = prefectureRef.value
  if (latitudeRef.value) query.latitude = latitudeRef.value.toString()
  if (longitudeRef.value) query.longitude = longitudeRef.value.toString()
  if (productsQueryRef.value.length) query.products = productsQueryRef.value.join(",")
  if (typeQueryRef.value.length) query.type = typeQueryRef.value.join(",")
  if (scaleQueryRef.value.length) query.scale = scaleQueryRef.value.join(",")
  if (othersSelectQueryRef.value.length) query.otherSelect = othersSelectQueryRef.value.join(",")
  return query
}

const keywordInput = () => {
  updateFilterValue()
  const element = document.getElementById("storeSearchTextInput")
  if (element) element.blur()
}

const updateFilterValue = () => {
  props.updateLocationError("")
  const query = createFilterQuery()
  navigateTo({ query })
}

const resetFilter = (): void => {
  productsQueryRef.value = []
  typeQueryRef.value = []
  scaleQueryRef.value = []
  othersSelectQueryRef.value = []
  prefectureRef.value = ""
  searchTextRef.value = ""
  latitudeRef.value = undefined
  longitudeRef.value = undefined
  updateFilterValue()
}

const csvToArray = (queryValue: string) => queryValue?.split(",").filter((v) => v !== "") ?? []
const initializeData = async () => {
  if (typeof route.query.geolocation === "string") {
    await setLocation()
    return
  }

  if (typeof route.query.products === "string") productsQueryRef.value = csvToArray(route.query.products)
  if (typeof route.query.type === "string") typeQueryRef.value = csvToArray(route.query.type)
  if (typeof route.query.scale === "string") scaleQueryRef.value = csvToArray(route.query.scale)
  if (typeof route.query.otherSelect === "string") othersSelectQueryRef.value = csvToArray(route.query.otherSelect)
  if (typeof route.query.prefecture === "string") prefectureRef.value = route.query.prefecture
  if (typeof route.query.searchText === "string") searchTextRef.value = route.query.searchText
  if (typeof route.query.latitude === "string") latitudeRef.value = parseFloat(route.query.latitude)
  if (typeof route.query.longitude === "string") longitudeRef.value = parseFloat(route.query.longitude)
}

const removeTag = (target: string, index: number) => {
  if (target === "products") productsQueryRef.value.splice(index, 1)
  if (target === "types") typeQueryRef.value.splice(index, 1)
  if (target === "scales") scaleQueryRef.value.splice(index, 1)
  if (target === "othersSelects") othersSelectQueryRef.value.splice(index, 1)
  updateFilterValue()
}

watch(
  () => route.fullPath,
  async () => {
    await initializeData()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped module>
.container {
  .location {
    display: flex;
    flex-flow: column;
    gap: 1rem;
    margin-bottom: 3rem;
    .head {
      display: flex;
      align-items: center;
      .title {
        color: $primary-100;
        margin-right: auto;
      }
      .clear {
        color: $black;
        text-decoration: underline;
      }
    }
    .location_button_area {
      height: 3.5rem;
      .location_button {
        height: 100%;
      }
    }
    .note {
      font-size: $font-size-12;
      color: $black-60;
    }
  }

  .sidebar {
    transform: translateX(-100%);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    width: 90vw;
    max-width: 300px;
    height: 100vh;
    height: 100dvh;
    overflow-y: auto;
    background-color: $white;

    transition: transform 0.3s ease;
    // フィルター背後の商品一覧スクロールを連動させない
    overscroll-behavior-y: none;
    &.sp_open {
      transform: translateX(0);
    }

    @include md {
      position: relative;
      flex-shrink: 0;
      transform: none;
      height: 100%;
      width: 100%;
      max-width: 100%;
      margin-right: 2rem;
    }

    // Macでスクロールバーを常時表示
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 4px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 0.25rem;
      background-color: $black-10;
      -webkit-box-shadow: 0 0 1px $black-10;
    }

    .close {
      position: absolute;
      top: 30px;
      right: 30px;
      padding: 15px;
      cursor: pointer;
      &::before,
      &::after {
        position: absolute;
        top: 14px;
        left: 0px;
        width: 32px;
        height: 1px;
        content: "";
        background: $primary;
      }
      &::before {
        transform: rotate(45deg);
      }
      &::after {
        transform: rotate(-45deg);
      }
      @include md {
        display: none;
      }
    }
    .filter_list {
      max-height: calc(100vh - 3.75rem);
      max-height: calc(100dvh - 3.75rem);
      overflow-y: auto;
      padding: 2rem 1rem;
      @include md {
        max-height: 85vh;
        padding: 0;
      }
      .filter_title {
        @include main-font;
        font-size: $font-size-16;
        display: block;
        margin-bottom: 1rem;
      }
      .clear {
        color: $black;
        text-decoration: underline;
        margin-top: 0.5rem;
      }
      .sp_sort {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-bottom: 1rem;
      }
      .list_item,
      .form_section {
        padding: 1rem 0;
        &:not(:last-child) {
          border-bottom: 1px solid $secondary-40;
        }
        .select_title {
          font-weight: bold;
        }
        .checkbox_list {
          display: flex;
          flex-direction: column;
          margin-top: 0.5rem;
          gap: 0.5rem;
          padding: 0.5rem 0;
          font-size: $font-size-14;
          .active {
            color: $primary;
            font-weight: bold;
            pointer-events: none;
          }
        }

        .item {
          font-weight: inherit;
          padding: 0.125rem;
        }
      }
      .single_filter_buttons {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem 0;
        font-size: $font-size-14;
      }
    }
    .submit {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 1rem 0;
      background-color: $primary;
      color: $white;
      font-weight: bold;
      justify-content: center;
      text-align: center;
      cursor: pointer;
      z-index: 101;
    }
  }
  .error_message {
    font-size: $font-size-14;
    color: $danger;
  }
}

.overlay_area {
  position: fixed;
  inset: 0px;
  z-index: 99;
  width: 100%;
  height: 100%;
  background-color: rgba($primary, 0.85);
  cursor: pointer;
}
</style>
